import React, { Dispatch, SetStateAction, useEffect, useState } from "react";
import { useAppSelector } from "@/redux/hooks/hooks";
import {
  useAddVideoAnalysisMutation,
  useGetVideoAnalysisQuery,
} from "@/redux/feature/videoDashboard/VideoDashboardApi";
import ErrorState from "@/pages/VideoDashboard/components/videoAnalysisModal/components/ErrorState";
import LoadingState from "@/pages/VideoDashboard/components/videoAnalysisModal/components/LoadingState";
import ResultsDisplay from "@/pages/VideoDashboard/components/videoAnalysisModal/components/ResultsDisplay";
import DisplayAnalysisResult from "./DisplayAnalysisResult";

interface ModalVideoAnalysisProps {
  videoUrl: string;
  currentTab: "videoDetails" | "generateScript" | "videoAnalysis";
  setCurrentTab: Dispatch<
    SetStateAction<"videoDetails" | "generateScript" | "videoAnalysis">
  >;
  thumbnail: string;
}

const ModalVideoAnalysis: React.FC<ModalVideoAnalysisProps> = ({
  videoUrl,
  currentTab,
  setCurrentTab,
  thumbnail,
}) => {
  const [taskId, setTaskId] = useState<string | null>(null);
  const [isAnalyzing, setIsAnalyzing] = useState(false);
  const [result, setResult] = useState<any>(null);
  const [error, setError] = useState<string | null>(null);
  const [viewingFromList, setViewingFromList] = useState(false);

  const { user } = useAppSelector((state) => state.auth);
  const [addVideoAnalysis] = useAddVideoAnalysisMutation();
  const { data: analysisData } = useGetVideoAnalysisQuery(
    { taskID: taskId },
    { skip: !taskId || !isAnalyzing, pollingInterval: 3000 }
  );

  // Start analysis automatically when component mounts
  useEffect(() => {
    if (videoUrl) {
      startAnalysis();
    }
  }, [videoUrl]);

  // Start analysis
  const startAnalysis = async () => {
    if (!videoUrl) {
      setError("No video URL provided");
      return;
    }

    try {
      setError(null);
      setIsAnalyzing(true);

      const response = await addVideoAnalysis({
        email: user?.email,
        videoUrl,
      }).unwrap();

      if (response.task_id) {
        setTaskId(response.task_id);
      } else {
        throw new Error("Failed to start analysis");
      }
    } catch (err: any) {
      setError(err.message || "Analysis failed");
      setIsAnalyzing(false);
    }
  };

  // Handle polling response
  React.useEffect(() => {
    if (!analysisData || !isAnalyzing) return;

    if (
      analysisData.status === "SUCCESS" ||
      analysisData.status === "completed"
    ) {
      setResult(analysisData.result);
      setIsAnalyzing(false);
    } else if (analysisData.status === "failed") {
      setError(analysisData.error || "Analysis failed");
      setIsAnalyzing(false);
    }
  }, [analysisData, isAnalyzing]);

  // Handle previous analysis selection
  const handleSelectPreviousAnalysis = (analysis: any) => {
    if (analysis.result) {
      setResult(analysis.result);
      setTaskId(analysis.video_url);
      setViewingFromList(true);
    } else {
      setError("No analysis results found");
    }
  };

  // Reset to initial state
  const resetAnalysis = () => {
    setTaskId(null);
    setResult(null);
    setError(null);
    setViewingFromList(false);
  };

  // Render states
  if (error) {
    return (
      <ErrorState
        message={error}
        onRetry={startAnalysis}
        onClose={() => setCurrentTab("videoDetails")}
      />
    );
  }

  if (isAnalyzing) {
    return <LoadingState onCancel={() => setIsAnalyzing(false)} />;
  }

  if (result) {
    return (
      // <ResultsDisplay
      //   thumbnail={thumbnail}
      //   result={result}
      //   taskId={taskId || undefined}
      //   onNewAnalysis={resetAnalysis}
      //   onClose={() => setCurrentTab("videoDetails")}
      //   showBackToList={viewingFromList}
      //   onBackToList={() => {
      //     setViewingFromList(false);
      //     setResult(null);
      //   }}
      //   showNewAnalysis={false}
      // />
      <DisplayAnalysisResult result={result} />
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
        <h2 className="text-xl sm:text-2xl font-semibold text-gray-900">
          Video Analysis
        </h2>
      </div>

      <div className="space-y-4">
        <div className="p-4 border border-gray-200 rounded-lg bg-gray-50">
          <p className="text-sm text-gray-700">
            Analyzing video: {videoUrl.split("/").pop()}
          </p>
          <button
            onClick={() => setCurrentTab("videoDetails")}
            className="flex-1 px-6 py-3 border border-gray-300 rounded-lg text-sm font-medium hover:bg-gray-100 transition"
          >
            Back to Details
          </button>
        </div>
      </div>
    </div>
  );
};

export default ModalVideoAnalysis;
